import { Alert, Button, Modal, Skeleton, Typography } from 'antd'
import { useContext, useState } from 'react'
import { AuthenticationContext } from '../../AuthenticationContext'
import { extractApiErrorMessage } from '../../helpers/apiErrorMessage'
import { useJobPricingSettingsQuery, useSetJobPricingSettingsMutation } from '../../domain/jobs/jobPricingSettingsQueries'
import SwitchWithDescription from '../common/SwitchWithDescription'
import VSpace from '../common/VSpace'
import { isTurnOnBlocked, needsSurchargeDisclaimer, toPaymentFeesView } from '../paymentFees/paymentFeesModel'
import PaymentFeesBlockedAlert from '../paymentFees/PaymentFeesBlockedAlert'
import PaymentFeesTable from '../paymentFees/PaymentFeesTable'
import SurchargeDisclaimer, { SURCHARGE_DISCLAIMER_OK_TEXT } from '../paymentFees/SurchargeDisclaimer'

type Props = {
  jobId: number
  onSaved?: () => void
  hasUnsavedChanges?: boolean
}

const JobPassOnPaymentFeesForJob = ({ jobId, onSaved, hasUnsavedChanges }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const hasAccess = currentUser?.roles.includes('Accounting') ?? false
  const query = useJobPricingSettingsQuery(jobId, hasAccess)
  const mutation = useSetJobPricingSettingsMutation(jobId)
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false)
  if (!hasAccess || !query.isFeatureEnabled) {
    return null
  }
  if (query.isLoading) {
    return <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 1 }} />
  }
  if (query.isError) {
    return <Alert type="error" message="Unable to load this job’s payment fee setting."
      action={<Button onClick={() => query.refetch()}>Retry</Button>} />
  }
  const settings = query.data
  if (!settings?.featureEnabled) {
    return null
  }
  const view = toPaymentFeesView(settings.cardSurcharge, settings.bankSurcharge)
  const saveErrorMessage = mutation.isError
    ? extractApiErrorMessage(mutation.error, 'Unable to save this job’s payment fee setting. Please try again.')
    : null
  const closeDisclaimer = () => {
    mutation.reset()
    setIsDisclaimerOpen(false)
  }
  const save = (enabled: boolean, acceptedSurchargeDisclaimer: boolean) => mutation.mutate(
    { enabled, acceptedSurchargeDisclaimer },
    { onSuccess: () => { setIsDisclaimerOpen(false); onSaved?.() } }
  )
  const handleToggle = () => {
    if (needsSurchargeDisclaimer(settings.enabled, settings.surchargeDisclaimerAccepted)) {
      mutation.reset()
      setIsDisclaimerOpen(true)
    } else {
      save(!settings.enabled, false)
    }
  }
  return <VSpace>
    <Typography.Title level={5}>Pass On Payment Fees</Typography.Title>
    <SwitchWithDescription
      label="Pass credit card fees on to the customer"
      description="Applies to invoices created from now on. The job’s prices don’t change. This choice does not change the company default or other jobs."
      checked={settings.enabled}
      disabled={isTurnOnBlocked(view, settings.enabled) || !settings.canEdit || mutation.isLoading || query.isFetching || hasUnsavedChanges}
      onClick={handleToggle}
    />
    <PaymentFeesBlockedAlert reason={view.blockedReason} />
    {!settings.canEdit && <Alert type="info" message={settings.editRestriction ?? 'This job’s payment fee setting cannot be changed right now.'} />}
    {hasUnsavedChanges && <Alert type="info" message="Save your job changes before changing whether payment fees are passed on." />}
    {saveErrorMessage && !isDisclaimerOpen && <Alert type="error" message={saveErrorMessage} />}
    {mutation.isLoading && <span role="status">Saving…</span>}
    <PaymentFeesTable rows={view.rows} />
    <Modal
      open={isDisclaimerOpen}
      title="Turn on the payment surcharge for this job?"
      okText={SURCHARGE_DISCLAIMER_OK_TEXT}
      onOk={() => save(true, true)}
      confirmLoading={mutation.isLoading}
      onCancel={closeDisclaimer}
    >
      <p>Customers pay a surcharge when they pay electronically on invoices created from now on. The job’s prices don’t change.</p>
      <SurchargeDisclaimer />
      {saveErrorMessage && <Alert type="error" showIcon message={saveErrorMessage} />}
    </Modal>
  </VSpace>
}

const JobPassOnPaymentFees = (props: Props) => <JobPassOnPaymentFeesForJob key={props.jobId} {...props} />

export default JobPassOnPaymentFees
